import React from 'react';
import { useTranslation } from 'react-i18next';

import { MediaElement } from 'editor/src/store/design/types';
import {
  FineDetailWarning,
  GradientWarning,
  SemiTransparentPixelsWarning,
  SmallTextWarning,
  ThinLineWarning,
  WarningType,
} from 'editor/src/store/editorModules/warnings/types';

import AbstractWarningItem from './AbstractWarningItem';

export type PrintRuleWarning =
  | SemiTransparentPixelsWarning
  | FineDetailWarning
  | SmallTextWarning
  | ThinLineWarning
  | GradientWarning;

export const printRuleWarningTypes = new Set<WarningType>([
  WarningType.SemiTransparentPixels,
  WarningType.FineDetail,
  WarningType.SmallText,
  WarningType.ThinLine,
  WarningType.Gradient,
]);

export const printRuleWarningKeys: { [type in PrintRuleWarning['type']]: string } = {
  [WarningType.SemiTransparentPixels]: 'editor-warning-semi-transparent',
  [WarningType.FineDetail]: 'editor-warning-fine-detail',
  [WarningType.SmallText]: 'editor-warning-small-text',
  [WarningType.ThinLine]: 'editor-warning-thin-line',
  [WarningType.Gradient]: 'editor-warning-gradient',
};

function getSizes(warning: PrintRuleWarning): { min?: number; size?: number } {
  switch (warning.type) {
    case WarningType.SmallText:
      return { min: warning.minTextHeightMm, size: warning.textHeightMm };
    case WarningType.ThinLine:
      return { min: warning.minLineWidthMm, size: warning.lineWidthMm };
    case WarningType.FineDetail:
      return { min: warning.minLineWidthMm };
    default:
      return {};
  }
}

interface Props {
  warning: PrintRuleWarning;
  element: MediaElement;
  selected: boolean;
}

// No fix action: making the element bigger, thicker or flatter is the buyer's call, and the checks only warn.
function PrintRuleWarningItem({ warning, element, selected }: Props) {
  const { t } = useTranslation();
  const key = printRuleWarningKeys[warning.type];
  const values = getSizes(warning);

  return (
    <AbstractWarningItem warning={warning} element={element} selected={selected} title={t(key)}>
      {t(`${key}-explanation`, values)}
    </AbstractWarningItem>
  );
}

export default React.memo(PrintRuleWarningItem);
